import { errorToastKey } from './toastKey';
import { App, message as staticMessage } from 'antd';
import type {
  ArgsProps,
  ConfigOptions as MessageConfig,
  MessageInstance,
  NoticeType,
} from 'antd/es/message/interface';
import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { SemanticStatusIcon } from './statusIcon';

export const TOAST_MESSAGE_CONFIG: MessageConfig = {
  duration: 3.5,
  maxCount: 3,
};

// Toast 状态图标走项目 design token:语义色只做轻量 tint,不再使用高饱和实心圆。
const TOAST_ICON: Record<Exclude<NoticeType, 'loading'>, ReactNode> = {
  success: <SemanticStatusIcon type="success" />,
  error: <SemanticStatusIcon type="error" />,
  info: <SemanticStatusIcon type="info" />,
  warning: <SemanticStatusIcon type="warning" />,
};

export type ToastContent = ReactNode;

export interface ToastApi {
  success: (content: ToastContent) => void;
  error: (content: ToastContent) => void;
  info: (content: ToastContent) => void;
  warning: (content: ToastContent) => void;
  loading: (content: ToastContent) => () => void;
  raw: MessageInstance;
}

const TOAST_DURATION: Record<Exclude<NoticeType, 'loading'>, number> = {
  success: 3.5,
  error: 5,
  info: 3,
  warning: 4,
};

let appMessageApi: MessageInstance | null = null;

const readableApis = new WeakMap<MessageInstance, MessageInstance>();

function decorateMessage(config: ArgsProps): ArgsProps {
  const type = config.type;
  if (!type) return config;
  const classNames = new Set((config.className || '').split(' ').filter(Boolean));
  classNames.add('qm-toast');
  classNames.add(`qm-toast-${type}`);
  return {
    ...config,
    key: config.key ?? (config.onClose || config.duration === 0
      ? undefined : errorToastKey(type, config.content)),
    duration: config.duration ?? (type === 'loading' ? TOAST_MESSAGE_CONFIG.duration : TOAST_DURATION[type]),
    icon: config.icon === undefined
      ? type === 'loading' ? <SemanticStatusIcon type="loading" /> : TOAST_ICON[type]
      : config.icon,
    className: Array.from(classNames).join(' '),
  };
}

export function readableMessageApi(api: MessageInstance): MessageInstance {
  const cached = readableApis.get(api);
  if (cached) return cached;
  const open: MessageInstance['open'] = (config) => api.open(decorateMessage(config));
  const notice = (type: NoticeType): MessageInstance['success'] => (content, duration, onClose) => {
    const config = content && typeof content === 'object' && 'content' in content
      ? content : { content };
    return open({
      duration: typeof duration === 'number' ? duration : undefined,
      onClose: typeof duration === 'function' ? duration : onClose,
      ...config,
      type,
    });
  };
  const wrapped: MessageInstance = {
    ...api, open,
    success: notice('success'), error: notice('error'),
    info: notice('info'), warning: notice('warning'), loading: notice('loading'),
  };
  readableApis.set(api, wrapped);
  readableApis.set(wrapped, wrapped);
  return wrapped;
}

export function createToastApi(message: MessageInstance): ToastApi {
  const raw = readableMessageApi(message);
  const open = (type: NoticeType, content: ToastContent) => raw.open({
    type,
    duration: type === 'loading' ? 0 : TOAST_DURATION[type],
    key: errorToastKey(type, content),
    content: (
      <span className="qm-toast-content" data-toast-status={type} data-tour={`toast-${type}`}>
        {content}
      </span>
    ),
  });
  return {
    success: (content) => { open('success', content); },
    error: (content) => { open('error', content); },
    info: (content) => { open('info', content); },
    warning: (content) => { open('warning', content); },
    loading: (content) => {
      const close = open('loading', content);
      return () => close();
    },
    raw,
  };
}

export function getToastMessageApi(): MessageInstance {
  return readableMessageApi(appMessageApi ?? (staticMessage as unknown as MessageInstance));
}

export function ToastBridge() {
  const { message } = App.useApp();

  useEffect(() => {
    appMessageApi = message;
    return () => {
      if (appMessageApi === message) appMessageApi = null;
    };
  }, [message]);

  return null;
}
